import type { ReactElement } from "react";
import type { FormFieldSpec } from "../types.js";

interface Props {
  fields: FormFieldSpec[];
  values: Record<string, unknown>;
  errors?: Record<string, string>;
  onChange: (id: string, value: unknown) => void;
  idPrefix?: string;
}

function enumValuesFor(field: FormFieldSpec, fields: FormFieldSpec[]): string[] | undefined {
  if (field.ref) return fields.find((f) => f.id === field.ref)?.values;
  return field.values;
}

/** Generic, accessible form renderer driven by manifest form fields. */
export function FormRenderer({ fields, values, errors = {}, onChange, idPrefix = "f" }: Props): ReactElement {
  return (
    <div>
      {fields.map((field) => {
        const fid = `${idPrefix}-${field.id}`;
        const label = field.label ?? field.id;
        const err = errors[field.id];
        const errId = err ? `${fid}-err` : undefined;
        const hintId = field.description ? `${fid}-hint` : undefined;
        const describedBy = [hintId, errId].filter(Boolean).join(" ") || undefined;
        const common = { id: fid, "aria-invalid": err ? true : undefined, "aria-describedby": describedBy };
        const enumValues = enumValuesFor(field, fields);
        const value = values[field.id];

        return (
          <div key={field.id} className="field">
            <label htmlFor={fid}>
              {label}
              {field.required ? <span aria-hidden="true"> *</span> : null}
            </label>
            {field.description ? (
              <p id={hintId} className="field-hint">
                {field.description}
              </p>
            ) : null}

            {(field.type === "enum" || field.type === "string") && enumValues ? (
              <select {...common} value={(value as string) ?? ""} onChange={(e) => onChange(field.id, e.target.value)}>
                <option value="">Select…</option>
                {enumValues.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            ) : field.type === "text" ? (
              <textarea {...common} value={(value as string) ?? ""} onChange={(e) => onChange(field.id, e.target.value)} />
            ) : field.type === "number" || field.type === "scale" ? (
              <input
                {...common}
                type="number"
                min={field.min}
                max={field.max}
                value={value === undefined ? "" : String(value)}
                onChange={(e) => onChange(field.id, e.target.value === "" ? undefined : Number(e.target.value))}
              />
            ) : field.type === "datetime" ? (
              <input
                {...common}
                type="datetime-local"
                value={(value as string) ?? ""}
                onChange={(e) => onChange(field.id, e.target.value)}
              />
            ) : field.type === "boolean" ? (
              <input
                {...common}
                type="checkbox"
                checked={Boolean(value)}
                onChange={(e) => onChange(field.id, e.target.checked)}
              />
            ) : field.type === "checkbox-set" || field.type === "multi-enum" ? (
              <fieldset {...common}>
                <legend className="sr-only">{label}</legend>
                {(enumValues ?? []).map((v) => {
                  const arr = Array.isArray(value) ? (value as string[]) : [];
                  return (
                    <label key={v} className="checkbox">
                      <input
                        type="checkbox"
                        checked={arr.includes(v)}
                        onChange={(e) => {
                          const next = e.target.checked ? [...arr, v] : arr.filter((x) => x !== v);
                          onChange(field.id, next);
                        }}
                      />
                      {v}
                    </label>
                  );
                })}
              </fieldset>
            ) : (
              <input {...common} type="text" value={(value as string) ?? ""} onChange={(e) => onChange(field.id, e.target.value)} />
            )}

            {err ? (
              <p id={errId} role="alert" className="field-error">
                {err}
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
